class CartStateManager {
    constructor() {
        this.state = {
            items: [],
            item_count: 0,
            total_price: 0,
            currency: "USD",
            cart_level_discount_applications: []
        };
        this.isUpdating = false;
        this.listeners = new Map;
        this.rateLimitCooldownUntil = 0;
        this.RATE_LIMIT_COOLDOWN_MS = 3e4;
        this.config = {
            autoRefreshInterval: null,
            debugMode: false
        };
        this.init();
    }
    async init() {
        this.log("CartStateManager initializing...");
        await this.refresh();
        this.setupEventListeners();
        this.log("CartStateManager initialized", this.state);
    }
    setupEventListeners() {
        document.addEventListener("cart:add", e => this.handleAdd(e.detail));
        document.addEventListener("cart:update", e => this.handleUpdate(e.detail));
        document.addEventListener("cart:remove", e => this.handleRemove(e.detail));
        document.addEventListener("cart:refresh", () => this.refresh());
    }
    isRateLimited() {
        return Date.now() < this.rateLimitCooldownUntil;
    }
    getRateLimitCooldownRemaining() {
        const remaining = this.rateLimitCooldownUntil - Date.now();
        return remaining > 0 ? Math.ceil(remaining / 1e3) : 0;
    }
    setRateLimitCooldown() {
        this.rateLimitCooldownUntil = Date.now() + this.RATE_LIMIT_COOLDOWN_MS;
        console.warn(`[CartStateManager] Rate limit detected - cooldown for ${this.RATE_LIMIT_COOLDOWN_MS / 1e3}s (until ${new Date(this.rateLimitCooldownUntil).toLocaleTimeString()})`);
    }
    async waitForRateLimitCooldown() {
        if (!this.isRateLimited()) return;
        const remaining = this.rateLimitCooldownUntil - Date.now();
        if (remaining > 0) {
            console.log(`[CartStateManager] Waiting ${Math.ceil(remaining / 1e3)}s for rate limit cooldown...`);
            await new Promise(resolve => setTimeout(resolve, remaining + 500));
        }
    }
    async refresh() {
        try {
            this.log("Refreshing cart state from Shopify...");
            const response = await fetch("/cart.js", {
                headers: {
                    Accept: "application/json"
                },
                credentials: "same-origin"
            });
            if (response.status === 429) {
                console.warn("[CartStateManager] Rate limited on refresh, using cached state");
                this.setRateLimitCooldown();
                return this.state;
            }
            if (!response.ok) {
                throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
            }
            const cart = await response.json();
            this.setState(cart);
            this.log("Cart state refreshed", cart);
            return cart;
        } catch (error) {
            console.error("[CartStateManager] Refresh error:", error);
            this.dispatchEvent("cart:error", {
                error: error,
                operation: "refresh"
            });
            throw error;
        }
    }
    setState(newState) {
        const oldState = {
            ...this.state
        };
        this.state = newState;
        const changes = this.getStateChanges(oldState, newState);
        this.log("Cart state updated", {
            newState: newState,
            changes: changes
        });
        this.dispatchEvent("cart:updated", {
            cart: newState,
            previousCart: oldState,
            changes: changes
        });
    }
    async add(items) {
        if (this.isUpdating) {
            this.log("Cart update in progress, queuing add operation");
            throw new Error("Cart update in progress");
        }
        this.isUpdating = true;
        this.dispatchEvent("cart:updating", {
            operation: "add"
        });
        try {
            const itemsArray = Array.isArray(items) ? items : [ items ];
            this.log("Adding items to cart", itemsArray);
            const response = await fetch("/cart/add.js", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json"
                },
                credentials: "same-origin",
                body: JSON.stringify({
                    items: itemsArray
                })
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.description || "Failed to add to cart");
            }
            const addedItems = await response.json();
            this.log("Items added successfully", addedItems);
            await this.refresh();
            this.dispatchEvent("cart:added", {
                items: addedItems
            });
            return addedItems;
        } catch (error) {
            console.error("[CartStateManager] Add error:", error);
            this.dispatchEvent("cart:error", {
                error: error,
                operation: "add",
                message: error.message
            });
            throw error;
        } finally {
            this.isUpdating = false;
        }
    }
    async addNested(parentItem, childItems = []) {
        if (this.isRateLimited()) {
            const remaining = this.getRateLimitCooldownRemaining();
            console.warn(`[CartStateManager] ⏳ Rate limit cooldown active - waiting ${remaining}s before cart add...`);
            await this.waitForRateLimitCooldown();
            console.log("[CartStateManager] ✅ Rate limit cooldown expired, proceeding with cart add");
        }
        console.log("[CartStateManager] 📦 addNested called with:");
        console.log("  Parent:", parentItem?.properties?.["_Item Type"] || "Unknown");
        console.log("  Children count:", childItems?.length || 0);
        if (childItems && childItems.length > 0) {
            childItems.forEach((child, idx) => {
                console.log(`    ${idx + 1}. ${child.properties?.["_Item Type"] || "Unknown"} (ID: ${child.id})`);
            });
            const hasBridge = childItems.some(c => c.properties?.["_Item Type"]?.includes("Bridge"));
            const hasMagnet = childItems.some(c => c.properties?.["_Item Type"]?.includes("Magnet"));
            console.log("[CartStateManager] 🔍 Hardware in children: Bridge=" + hasBridge + ", Magnet=" + hasMagnet);
        }
        const fetchWithRetry = async (url, options, maxRetries = 3) => {
            for (let attempt = 1; attempt <= maxRetries; attempt++) {
                const response = await fetch(url, options);
                if (response.status === 429) {
                    this.setRateLimitCooldown();
                    const waitTime = 5e3 * attempt;
                    console.warn(`[CartStateManager] Rate limited (429), waiting ${waitTime}ms before retry ${attempt}/${maxRetries}`);
                    await new Promise(resolve => setTimeout(resolve, waitTime));
                    continue;
                }
                return response;
            }
            throw new Error("Rate limit exceeded after retries");
        };
        if (this.isUpdating) {
            this.log("Cart update in progress, queuing nested add operation");
            throw new Error("Cart update in progress");
        }
        this.isUpdating = true;
        this.dispatchEvent("cart:updating", {
            operation: "add-nested"
        });
        try {
            this.log("Adding parent item first", parentItem);
            const progressTotal = 1 + (childItems ? childItems.length : 0);
            const emitProgress = (index, item) => {
                this.dispatchEvent("cart:add-progress", {
                    index: index,
                    total: progressTotal,
                    itemType: item?.properties?.["_Item Type"] || ""
                });
            };
            emitProgress(1, parentItem);
            const parentResponse = await fetchWithRetry("/cart/add.js", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json"
                },
                credentials: "same-origin",
                body: JSON.stringify({
                    id: parentItem.id,
                    quantity: parentItem.quantity || 1,
                    properties: parentItem.properties || {}
                })
            });
            if (!parentResponse.ok) {
                let errorMsg = "Failed to add parent item";
                try {
                    const errorData = await parentResponse.json();
                    errorMsg = errorData.description || errorMsg;
                } catch (e) {}
                throw new Error(errorMsg);
            }
            const parentData = await parentResponse.json();
            this.log("Parent item added", parentData);
            const childResults = [];
            const failedChildren = [];
            if (childItems && childItems.length > 0) {
                this.log(`Adding ${childItems.length} child items SEQUENTIALLY with delays`);
                for (let i = 0; i < childItems.length; i++) {
                    const childItem = childItems[i];
                    const itemType = childItem.properties?.["_Item Type"] || "unknown";
                    const delayMs = 750;
                    console.log(`[CartStateManager] Waiting ${delayMs}ms before adding child ${i + 1}...`);
                    await new Promise(resolve => setTimeout(resolve, delayMs));
                    console.log(`[CartStateManager] Adding child ${i + 1}/${childItems.length}: ${itemType}`);
                    emitProgress(i + 2, childItem);
                    try {
                        const childResponse = await fetchWithRetry("/cart/add.js", {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json",
                                Accept: "application/json"
                            },
                            credentials: "same-origin",
                            body: JSON.stringify({
                                id: childItem.id,
                                quantity: childItem.quantity || 1,
                                properties: childItem.properties || {},
                                ...childItem.nestUnderParent === false ? {} : {
                                    parent_line_key: parentData.key
                                }
                            })
                        });
                        if (!childResponse.ok) {
                            let errorMsg = "Unknown error";
                            try {
                                const errorData = await childResponse.json();
                                errorMsg = errorData.description || errorMsg;
                            } catch (e) {}
                            console.warn(`[CartStateManager] ❌ Child item ${i + 1} (${itemType}) add failed:`, errorMsg, "Status:", childResponse.status);
                            failedChildren.push({
                                item: childItem,
                                error: errorMsg,
                                index: i
                            });
                        } else {
                            const childData = await childResponse.json();
                            console.log(`[CartStateManager] ✅ Child item ${i + 1} (${itemType}) added successfully. Key:`, childData.key);
                            childResults.push(childData);
                            if (childItem.nestUnderParent === false) {
                                console.log(`[CartStateManager] Added ${itemType} top-level by design (must outlive its group's clips)`);
                            } else if (!childData.parent_relationship) {
                                console.warn("[CartStateManager] ⚠️ No parent_relationship in response. Nesting may not be supported on this store version.");
                            }
                        }
                    } catch (childError) {
                        console.warn(`[CartStateManager] Child item error:`, childError);
                        failedChildren.push({
                            item: childItem,
                            error: childError.message,
                            index: i
                        });
                    }
                }
                if (failedChildren.length > 0) {
                    console.warn(`[CartStateManager] ${failedChildren.length} child items failed. Retrying with longer delays...`);
                    for (const failedItem of failedChildren) {
                        await new Promise(resolve => setTimeout(resolve, 2e3));
                        try {
                            console.log(`[CartStateManager] Retrying child item: ${failedItem.item.properties?.["_Item Type"] || "unknown"}`);
                            const retryResponse = await fetchWithRetry("/cart/add.js", {
                                method: "POST",
                                headers: {
                                    "Content-Type": "application/json",
                                    Accept: "application/json"
                                },
                                credentials: "same-origin",
                                body: JSON.stringify({
                                    id: failedItem.item.id,
                                    quantity: failedItem.item.quantity || 1,
                                    properties: failedItem.item.properties || {},
                                    parent_line_key: parentData.key
                                })
                            });
                            if (retryResponse.ok) {
                                const retryData = await retryResponse.json();
                                console.log(`[CartStateManager] ✅ Retry successful for child item`);
                                childResults.push(retryData);
                            } else {
                                const errorData = await retryResponse.json().catch(() => ({}));
                                console.error(`[CartStateManager] ❌ Child item retry FAILED:`, errorData.description || "Unknown error");
                            }
                        } catch (retryError) {
                            console.error(`[CartStateManager] ❌ Child item retry error:`, retryError);
                        }
                    }
                }
                if (childResults.length !== childItems.length) {
                    const missingCount = childItems.length - childResults.length;
                    console.error(`[CartStateManager] ⚠️ WARNING: ${missingCount} of ${childItems.length} child items FAILED to add to cart!`);
                    const addedTypes = childResults.map(r => r.properties?.["_Item Type"] || "");
                    const expectedTypes = childItems.map(c => c.properties?.["_Item Type"] || "");
                    const missingTypes = expectedTypes.filter(type => {
                        const idx = addedTypes.indexOf(type);
                        if (idx !== -1) {
                            addedTypes.splice(idx, 1);
                            return false;
                        }
                        return true;
                    });
                    const missingBridge = missingTypes.some(t => t.includes("Bridge Hardware"));
                    const missingMagnet = missingTypes.some(t => t.includes("Magnet Hardware"));
                    const missingPaidLens = missingTypes.some(t => t.includes("Prescription Lens")) && childItems.some(c => c.properties?.["_Item Type"]?.includes("Prescription Lens") && c.properties?.["_Is_Paid_Upgrade"] === "true");
                    const hasCriticalFailure = missingBridge || missingMagnet || missingPaidLens;
                    if (hasCriticalFailure) {
                        console.error(`[CartStateManager] 🚨 CRITICAL: Missing items (Bridge=${missingBridge}, Magnet=${missingMagnet}, PaidLens=${missingPaidLens}) — rolling back parent and children`);
                        try {
                            console.log("[CartStateManager] Rolling back parent:", parentData.key);
                            await fetch("/cart/change.js", {
                                method: "POST",
                                headers: {
                                    "Content-Type": "application/json",
                                    Accept: "application/json"
                                },
                                credentials: "same-origin",
                                body: JSON.stringify({
                                    id: parentData.key,
                                    quantity: 0
                                })
                            });
                            for (const child of childResults) {
                                console.log("[CartStateManager] Rolling back child:", child.key, child.properties?.["_Item Type"]);
                                await fetch("/cart/change.js", {
                                    method: "POST",
                                    headers: {
                                        "Content-Type": "application/json",
                                        Accept: "application/json"
                                    },
                                    credentials: "same-origin",
                                    body: JSON.stringify({
                                        id: child.key,
                                        quantity: 0
                                    })
                                });
                            }
                            console.log("[CartStateManager] ✅ Rollback complete — parent + children removed");
                        } catch (rollbackError) {
                            console.error("[CartStateManager] Rollback error:", rollbackError);
                        }
                        this.dispatchEvent("cart:critical-add-failure", {
                            missingBridge: missingBridge,
                            missingMagnet: missingMagnet,
                            missingPaidLens: missingPaidLens,
                            missingTypes: missingTypes,
                            message: missingPaidLens && !missingBridge && !missingMagnet ? "A required lens upgrade could not be added. The order has been removed from your cart to prevent being undercharged." : "Required items could not be added. The order has been removed from your cart to prevent an incomplete order."
                        });
                        throw new Error("CRITICAL_ADD_FAILURE: Required items (hardware or paid lens) could not be added to cart");
                    }
                    this.dispatchEvent("cart:partial-add-failure", {
                        expectedChildren: childItems.length,
                        addedChildren: childResults.length,
                        missingCount: missingCount,
                        missingTypes: missingTypes,
                        message: `${missingCount} item(s) may not have been added to your cart. Please verify your cart before checkout.`
                    });
                }
            }
            this.log("All items added successfully", {
                parent: parentData,
                children: childResults
            });
            await this.refresh();
            this.dispatchEvent("cart:added", {
                parent: parentData,
                children: childResults
            });
            return {
                parent: parentData,
                children: childResults
            };
        } catch (error) {
            console.error("[CartStateManager] Nested add error:", error);
            this.dispatchEvent("cart:error", {
                error: error,
                operation: "add-nested",
                message: error.message
            });
            throw error;
        } finally {
            this.isUpdating = false;
        }
    }
    async updateQuantity(lineItemKey, quantity) {
        if (this.isUpdating) {
            throw new Error("Cart update in progress");
        }
        this.isUpdating = true;
        this.dispatchEvent("cart:updating", {
            operation: "update"
        });
        try {
            this.log("Updating item quantity", {
                lineItemKey: lineItemKey,
                quantity: quantity
            });
            const response = await fetch("/cart/change.js", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json"
                },
                credentials: "same-origin",
                body: JSON.stringify({
                    id: lineItemKey,
                    quantity: quantity
                })
            });
            if (!response.ok) {
                throw new Error("Failed to update quantity");
            }
            const cart = await response.json();
            this.setState(cart);
            this.log("Quantity updated", {
                lineItemKey: lineItemKey,
                quantity: quantity
            });
            this.dispatchEvent("cart:quantity:updated", {
                lineItemKey: lineItemKey,
                quantity: quantity
            });
            return cart;
        } catch (error) {
            console.error("[CartStateManager] Update error:", error);
            this.dispatchEvent("cart:error", {
                error: error,
                operation: "update",
                lineItemKey: lineItemKey,
                quantity: quantity
            });
            throw error;
        } finally {
            this.isUpdating = false;
        }
    }
    async remove(lineItemKey) {
        return this.updateQuantity(lineItemKey, 0);
    }
    async clear() {
        if (this.isUpdating) {
            throw new Error("Cart update in progress");
        }
        this.isUpdating = true;
        this.dispatchEvent("cart:updating", {
            operation: "clear"
        });
        try {
            this.log("Clearing cart");
            const response = await fetch("/cart/clear.js", {
                method: "POST",
                headers: {
                    Accept: "application/json"
                },
                credentials: "same-origin"
            });
            if (!response.ok) {
                throw new Error("Failed to clear cart");
            }
            await this.refresh();
            this.log("Cart cleared");
            this.dispatchEvent("cart:cleared", {});
            return this.state;
        } catch (error) {
            console.error("[CartStateManager] Clear error:", error);
            this.dispatchEvent("cart:error", {
                error: error,
                operation: "clear"
            });
            throw error;
        } finally {
            this.isUpdating = false;
        }
    }
    getState() {
        return {
            ...this.state
        };
    }
    get(property) {
        return this.state[property];
    }
    getStateChanges(oldState, newState) {
        return {
            itemCountChanged: oldState.item_count !== newState.item_count,
            priceChanged: oldState.total_price !== newState.total_price,
            itemsChanged: oldState.items.length !== newState.items.length,
            itemCountDelta: newState.item_count - oldState.item_count,
            priceDelta: newState.total_price - oldState.total_price
        };
    }
    dispatchEvent(eventName, detail = {}) {
        const event = new CustomEvent(eventName, {
            detail: {
                ...detail,
                timestamp: Date.now()
            },
            bubbles: true
        });
        document.dispatchEvent(event);
        this.log(`Event: ${eventName}`, detail);
    }
    async handleAdd(detail) {
        try {
            await this.add(detail.items);
        } catch (error) {
            console.error("[CartStateManager] Handle add error:", error);
        }
    }
    async handleUpdate(detail) {
        try {
            await this.updateQuantity(detail.lineItemKey, detail.quantity);
        } catch (error) {
            console.error("[CartStateManager] Handle update error:", error);
        }
    }
    async handleRemove(detail) {
        try {
            await this.remove(detail.lineItemKey);
        } catch (error) {
            console.error("[CartStateManager] Handle remove error:", error);
        }
    }
    log(message, data = null) {
        if (!this.config.debugMode) return;
        const timestamp = (new Date).toISOString().split("T")[1].slice(0, -1);
        if (data) {} else {}
    }
    setDebugMode(enabled) {
        this.config.debugMode = enabled;
        this.log(`Debug mode ${enabled ? "enabled" : "disabled"}`);
    }
}

if (typeof window !== "undefined") {
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", () => {
            window.cartStateManager = new CartStateManager;
        });
    } else {
        window.cartStateManager = new CartStateManager;
    }
}